import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Autocomplete,
  TextField,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { supabase } from '../../supabaseClient.ts';
import DifficultyPillSlider from './DifficultyPillSlider.tsx';
import MarksSelect from './MarksSelect.tsx';
import { DifficultyBucket, matchesDifficultyBucket } from './difficulty.ts';
import { MarksBucket, getMarksFilterRange, matchesMarksBucket } from './marks.ts';

/* ───────── types ───────── */
export type YearRow = { id: string; year: string | number };
export type Subject = { id: string; name: string; year_id: string | null };
export type Category = {
  id: string;
  name: string;
  parent_category_id: string | null;
  subject_id: string | null;
};
export type Tag = { id: string; name: string };

export type FilterState = {
  years: YearRow[];
  subjects: Subject[];
  topics: Category[];
  subtopics: Category[];
  types: Array<'mc' | 'input'>;
  tags: Tag[];
  difficulty: DifficultyBucket | null;
  marks: MarksBucket | null;
};

const dbTypesForFilter = (type: 'mc' | 'input') => (type === 'mc' ? ['mc', 'table'] : ['input']);

export type QuestionFiltersProps = {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterState) => void | Promise<void>;
  initialFilters?: FilterState;
  userId: string | null;
  loading?: boolean;
};

const QuestionFilters: React.FC<QuestionFiltersProps> = ({
  open,
  onClose,
  onApply,
  initialFilters,
  userId,
  loading: externalLoading = false,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [years, setYears] = useState<YearRow[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [cats, setCats] = useState<Category[]>([]);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [tagOptions, setTagOptions] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);

  // Draft selections
  const [draftYears, setDraftYears] = useState<YearRow[]>(initialFilters?.years ?? []);
  const [draftSubjects, setDraftSubjects] = useState<Subject[]>(initialFilters?.subjects ?? []);
  const [draftTopics, setDraftTopics] = useState<Category[]>(initialFilters?.topics ?? []);
  const [draftSubtopics, setDraftSubtopics] = useState<Category[]>(initialFilters?.subtopics ?? []);
  const [draftTypes, setDraftTypes] = useState<Array<'mc' | 'input'>>(
    initialFilters?.types ?? ['mc', 'input']
  );
  const [draftTags, setDraftTags] = useState<Tag[]>(initialFilters?.tags ?? []);
  const [draftDifficulty, setDraftDifficulty] = useState<DifficultyBucket | null>(
    initialFilters?.difficulty ?? null
  );
  const [draftMarks, setDraftMarks] = useState<MarksBucket | null>(
    initialFilters?.marks ?? null
  );

  // Reset drafts when dialog opens
  useEffect(() => {
    if (open && initialFilters) {
      setDraftYears(initialFilters.years);
      setDraftSubjects(initialFilters.subjects);
      setDraftTopics(initialFilters.topics);
      setDraftSubtopics(initialFilters.subtopics);
      setDraftTypes(initialFilters.types);
      setDraftTags(initialFilters.tags);
      setDraftDifficulty(initialFilters.difficulty ?? null);
      setDraftMarks(initialFilters.marks ?? null);
    }
  }, [open, initialFilters]);

  // Load years, subjects, categories, tags
  useEffect(() => {
    if (!userId) return;

    (async () => {
      setLoading(true);

      // Get student id from students table
      const { data: student } = await supabase
        .from('students')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (!student) {
        setLoading(false);
        return;
      }

      const studentId = student.id;

      // subjects for student, else the whole shared bank
      const { data: ss } = await supabase
        .from('student_subjects')
        .select('subject_id')
        .eq('student_id', studentId);

      const subjectIds = (ss ?? []).map((r) => r.subject_id);

      let subjectQuery = supabase.from('subjects').select('id, name, year_id');
      if (subjectIds.length) subjectQuery = subjectQuery.in('id', subjectIds);
      const { data: subjectData } = await subjectQuery;
      const subjectsRows: Subject[] = ((subjectData ?? []) as any[]).map((r) => ({
        id: r.id,
        name: r.name,
        year_id: r.year_id ?? null,
      }));
      setSubjects(subjectsRows);

      // Load the entire category tree reachable from these subjects (any depth)
      if (subjectsRows.length) {
        const subjIds = subjectsRows.map((s) => s.id);

        const { data: rootTopics, error: rootErr } = await supabase
          .from('categories')
          .select('id, name, parent_category_id, subject_id')
          .in('subject_id', subjIds)
          .is('parent_category_id', null);

        if (rootErr) {
          console.error(rootErr.message);
          setCats([]);
        } else {
          const all: Record<string, Category> = {};
          const queue: string[] = [];

          (rootTopics ?? []).forEach((c: any) => {
            all[c.id] = c as Category;
            queue.push(c.id);
          });

          // BFS down: fetch children repeatedly until no more
          while (queue.length) {
            const batchParentIds = Array.from(new Set(queue.splice(0, 50)));
            const { data: kids, error: kidsErr } = await supabase
              .from('categories')
              .select('id, name, parent_category_id, subject_id')
              .in('parent_category_id', batchParentIds);

            if (kidsErr) {
              console.error(kidsErr.message);
              break;
            }
            const newChildren: string[] = [];
            (kids ?? []).forEach((c: any) => {
              if (!all[c.id]) {
                all[c.id] = c as Category;
                if (c.parent_category_id) newChildren.push(c.id);
              }
            });
            if (newChildren.length) queue.push(...newChildren);
          }

          setCats(Object.values(all));
        }
      } else {
        setCats([]);
      }

      // Years are a shared global bank, not company-scoped
      const { data: yrs } = await supabase
        .from('years')
        .select('id, year')
        .order('year', { ascending: true });
      setYears((yrs ?? []) as YearRow[]);

      // Load *all* tags once; we'll scope them later based on selections
      const { data: tagsRows } = await supabase.from('tags').select('id, name').order('name');
      setAllTags((tagsRows ?? []) as Tag[]);
      setTagOptions((tagsRows ?? []) as Tag[]); // initial default

      setLoading(false);
    })();
  }, [userId]);

  // Limit Subjects by selected draft Years (if any)
  const draftSubjectOptions = useMemo(() => {
    if (!draftYears.length) return subjects;
    const allowedYearIds = new Set(draftYears.map((y) => y.id));
    return subjects.filter((s) => (s.year_id ? allowedYearIds.has(s.year_id) : false));
  }, [subjects, draftYears]);

  const draftTopicOptions = useMemo(() => {
    const allowedSubjectIds = new Set(
      (draftSubjects.length ? draftSubjects : draftSubjectOptions).map((s) => s.id),
    );
    return cats.filter(
      (c) =>
        c.parent_category_id === null &&
        (c.subject_id ? allowedSubjectIds.has(c.subject_id) : false),
    );
  }, [cats, draftSubjects, draftSubjectOptions]);

  const draftSubtopicOptions = useMemo(() => {
    const tIds = new Set(draftTopics.map((t) => t.id));
    if (!tIds.size) return [];
    return cats.filter((c) => c.parent_category_id && tIds.has(c.parent_category_id));
  }, [cats, draftTopics]);

  // Build category IDs for tag scoping
  const buildCategoryIds = useCallback((
    topicsSel: Category[],
    subtopicsSel: Category[],
    subjectsSel: Subject[],
    yearsSel: YearRow[],
  ) => {
    if (subtopicsSel.length) return subtopicsSel.map((s) => s.id);

    if (topicsSel.length) {
      const tIds = new Set(topicsSel.map((t) => t.id));
      const result = new Set<string>();

      topicsSel.forEach((t) => result.add(t.id));

      const byParent: Record<string, string[]> = {};
      cats.forEach((c) => {
        if (c.parent_category_id) {
          (byParent[c.parent_category_id] ||= []).push(c.id);
        }
      });

      const queue = Array.from(tIds);
      while (queue.length) {
        const parent = queue.shift()!;
        const kids = byParent[parent] || [];
        for (const kid of kids) {
          if (!result.has(kid)) {
            result.add(kid);
            queue.push(kid);
          }
        }
      }
      return Array.from(result);
    }

    const effectiveSubjects: Subject[] = subjectsSel.length
      ? subjectsSel
      : yearsSel.length
        ? subjects.filter((s) => s.year_id && yearsSel.some((y) => y.id === s.year_id))
        : [];

    if (effectiveSubjects.length) {
      const subjSet = new Set(effectiveSubjects.map((s) => s.id));
      const result = new Set<string>();

      const byParent: Record<string, string[]> = {};
      const rootsForSubject = new Map<string, string[]>();
      cats.forEach((c) => {
        if (c.parent_category_id) {
          (byParent[c.parent_category_id] ||= []).push(c.id);
        } else if (c.subject_id) {
          (rootsForSubject[c.subject_id] ||= []).push(c.id);
        }
      });

      for (const s of subjSet) {
        const roots = rootsForSubject.get(s) || [];
        const queue = [...roots];
        roots.forEach((r) => result.add(r));
        while (queue.length) {
          const p = queue.shift()!;
          const kids = byParent[p] || [];
          for (const k of kids) {
            if (!result.has(k)) {
              result.add(k);
              queue.push(k);
            }
          }
        }
      }
      return Array.from(result);
    }

    return cats.map((c) => c.id);
  }, [cats, subjects]);

  // Scope tag options (draft) to selection
  useEffect(() => {
    (async () => {
      const categoryIds = buildCategoryIds(draftTopics, draftSubtopics, draftSubjects, draftYears);
      if (!categoryIds.length) {
        setTagOptions(allTags);
        return;
      }

      setTagsLoading(true);
      try {
        let qt = supabase
          .from('question_tags')
          .select('tag_id, questions!inner(category_id, type, group_id, difficulty, marks)')
          .in('questions.category_id', categoryIds);

        if (draftTypes.length === 1) {
          qt = qt.in('questions.type', dbTypesForFilter(draftTypes[0]));
        }
        if (draftMarks) {
          const range = getMarksFilterRange(draftMarks);
          qt = qt.gte('questions.marks', range.min);
          if (range.max !== null) qt = qt.lte('questions.marks', range.max);
        }

        const { data: qtRows, error: qtErr } = await qt;
        if (qtErr) {
          console.error(qtErr.message);
          setTagOptions(allTags);
          return;
        }

        const scopedQuestionTagRows = (qtRows ?? []).filter(
          (r: any) =>
            matchesDifficultyBucket(r.questions?.difficulty, draftDifficulty) &&
            matchesMarksBucket(r.questions?.marks, draftMarks),
        );

        const tagIds = Array.from(
          new Set(scopedQuestionTagRows.map((r: any) => r.tag_id)),
        ).filter(Boolean);
        if (!tagIds.length) {
          setTagOptions([]);
          setDraftTags([]);
          return;
        }

        const { data: scopedTags, error: tagsErr } = await supabase
          .from('tags')
          .select('id, name')
          .in('id', tagIds)
          .order('name');

        if (tagsErr) {
          console.error(tagsErr.message);
          setTagOptions(allTags);
          return;
        }

        const scoped = (scopedTags ?? []) as Tag[];
        setTagOptions(scoped);
        setDraftTags((prev) => prev.filter((t) => scoped.some((s) => s.id === t.id)));
      } finally {
        setTagsLoading(false);
      }
    })();
  }, [draftYears, draftSubjects, draftTopics, draftSubtopics, draftTypes, draftDifficulty, draftMarks, cats, allTags, buildCategoryIds]);

  const handleClear = () => {
    setDraftYears([]);
    setDraftSubjects([]);
    setDraftTopics([]);
    setDraftSubtopics([]);
    setDraftTags([]);
    setDraftTypes(['mc', 'input']);
    setDraftDifficulty(null);
    setDraftMarks(null);
    setTagOptions(allTags);
  };

  const handleApply = async () => {
    const filters: FilterState = {
      years: draftYears,
      subjects: draftSubjects,
      topics: draftTopics,
      subtopics: draftSubtopics,
      types: draftTypes,
      tags: draftTags,
      difficulty: draftDifficulty,
      marks: draftMarks,
    };
    await onApply(filters);
    onClose();
  };

  const isLoading = loading || externalLoading;

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth>
      <DialogTitle>Filters</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Years */}
          <Autocomplete
            multiple
            options={years}
            value={draftYears}
            onChange={(_, v) => {
              setDraftYears(v);
              setDraftSubjects((prev) =>
                prev.filter((s) => s.year_id && v.some((yr) => yr.id === s.year_id)),
              );
              setDraftTopics([]);
              setDraftSubtopics([]);
            }}
            getOptionLabel={(o) => String(o.year)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(p) => <TextField {...p} label="Years" placeholder="Select years" />}
            disabled={isLoading || years.length === 0}
          />

          <Autocomplete
            multiple
            options={draftSubjectOptions}
            value={draftSubjects}
            onChange={(_, v) => {
              setDraftSubjects(v);
              setDraftTopics([]);
              setDraftSubtopics([]);
            }}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(p) => (
              <TextField {...p} label="Subjects" placeholder="Select subjects" />
            )}
            disabled={isLoading || draftSubjectOptions.length === 0}
          />

          <Autocomplete
            multiple
            options={draftTopicOptions}
            value={draftTopics}
            onChange={(_, v) => {
              setDraftTopics(v);
              setDraftSubtopics([]);
            }}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(p) => <TextField {...p} label="Topics" placeholder="Select topics" />}
            disabled={isLoading || draftTopicOptions.length === 0}
          />

          <Autocomplete
            multiple
            options={draftSubtopicOptions}
            value={draftSubtopics}
            onChange={(_, v) => setDraftSubtopics(v)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(p) => (
              <TextField {...p} label="Subtopics" placeholder="Select subtopics" />
            )}
            disabled={isLoading || draftTopics.length === 0}
          />
          {draftTopics.length > 0 && draftSubtopicOptions.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No subtopics under the selected topics (yet).
            </Typography>
          )}

          {/* Tags filter */}
          <Autocomplete
            multiple
            options={tagOptions}
            value={draftTags}
            onChange={(_, v) => setDraftTags(v)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(p) => (
              <TextField
                {...p}
                label="Tags"
                placeholder={
                  tagsLoading
                    ? 'Loading tags…'
                    : tagOptions.length
                      ? 'Select tags'
                      : 'No tags for current selection'
                }
              />
            )}
            disabled={isLoading || tagsLoading}
          />
          <Typography variant="caption" color="text.secondary">
            Tags are limited to the selected {draftYears.length ? 'year(s), ' : ''}subject/topic/subtopic
            {draftTypes.length === 1 ? ' and type' : ''}.
          </Typography>

          {/* Question type filter */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Stack sx={{ flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Question type
              </Typography>
              <ToggleButtonGroup
                value={draftTypes}
                onChange={(_, v: Array<'mc' | 'input'> | null) => setDraftTypes(v ?? [])}
                aria-label="question type filter"
              >
                <ToggleButton value="mc" aria-label="multiple choice">
                  Multi-choice
                </ToggleButton>
                <ToggleButton value="input" aria-label="short answer">
                  Short answer
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Stack
              direction="row"
              spacing={2}
              alignItems="stretch"
              sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
              flexWrap="wrap"
              useFlexGap
            >
              <Box sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}>
                <MarksSelect
                  value={draftMarks}
                  onChange={setDraftMarks}
                  disabled={isLoading}
                />
              </Box>
              <Box sx={{ width: { xs: '100%', sm: 220 }, flexShrink: 0 }}>
                <DifficultyPillSlider
                  value={draftDifficulty}
                  onChange={setDraftDifficulty}
                  disabled={isLoading}
                />
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleClear}>Clear</Button>
        <Button variant="contained" onClick={handleApply}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuestionFilters;

